import { ChampsOrigine } from '../components/tracabilite/ChampsOrigine';
import { champsTraceContact } from '../lib/tracabilite';
import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Search, UserPlus, Pencil, Trash2, QrCode } from '../lib/icons';
import { toast } from 'sonner';
import { fetchContactsRoles, updateContact, deleteContact, fetchCompteTiers, TYPES_CONTRAT, TYPES_TIERS } from '../lib/dataProvider';
import { PageTitle } from '../components/shared/badges';
import { GrandLivreTiers } from '../components/releves/GrandLivreTiers';
import { QrSoldeDialog } from '../components/releves/QrSoldeDialog';
import { ContactQuickForm } from '../components/reception/ContactQuickForm';
import { ImportContactsButton } from '../components/admin/ImportContactsButton';
import { useAuth } from '../lib/auth';
import { estAdmin } from '../lib/permissions';
import { rattacherAssocie } from '../lib/cca';
import { sansPerte } from '../lib/conflitEcriture';

const ONGLETS = [
  { id: 'tous', label: 'Tous', hint: 'Toute la base' },
  { id: 'client', label: 'Clients', hint: 'Prestation de trituration' },
  { id: 'fournisseur', label: 'Fournisseurs', hint: 'Vente d’olives' },
  { id: 'agriculteur', label: 'Agriculteurs', hint: 'Client ET fournisseur' },
  { id: 'associe', label: 'Associés', hint: 'Comptes courants d’associés' },
  { id: 'fournisseur_general', label: 'Fournisseurs généraux', hint: 'Factures de frais généraux' },
  { id: 'personnel', label: 'Personnel', hint: 'Salariés & saisonniers' },
];

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const fmtTnd = (v) => (Number(v) || 0).toFixed(3);

export default function Contacts() {
  const { user } = useAuth();
  const admin = estAdmin(user?.role);
  const [contacts, setContacts] = useState([]);
  const [onglet, setOnglet] = useState('tous');
  const [livre, setLivre] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [nouveau, setNouveau] = useState(false);
  const [edition, setEdition] = useState(null);
  const [qrSolde, setQrSolde] = useState(null);

  const load = useCallback(() => {
    fetchContactsRoles().then(setContacts);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const basculer = async (c, champ) => {
    const actuel = champ === 'estClient' ? c.estClient : c.estFournisseur;
    await updateContact(c.id, { [champ]: !actuel });
    load();
  };

  const ouvrirExtrait = async (c) => {
    const compte = await fetchCompteTiers(c.id);
    setLivre(compte);
  };

  const enregistrerEdition = async (e) => {
    e.preventDefault();
    if (!edition.nom.trim()) return toast.error('Le nom est obligatoire.');
    if (edition.personnel) {
      if (!edition.cin.trim()) return toast.error('Le N° CIN est obligatoire pour un salarié.');
      if (!edition.cnss.trim()) return toast.error('Le N° CNSS est obligatoire pour un salarié.');
    }
    const texteTarif = String(edition.tarifTriturationPersonnalise ?? '').trim();
    const tarifClient = texteTarif === '' ? null : Number(texteTarif);
    if (admin && edition.estClient && texteTarif !== '' && (!Number.isFinite(tarifClient) || tarifClient <= 0))
      return toast.error('Saisissez un tarif strictement positif ou laissez vide pour le tarif standard.');
    const { error: refus } = await sansPerte(() => updateContact(edition.id, {
      ...(!edition.personnel ? champsTraceContact(edition) : {}),
      nom: edition.nom.trim(),
      region: edition.region.trim() || '—',
      tel: edition.tel.trim(),
      adresse: edition.adresse.trim(),
      mf: edition.personnel ? '' : edition.mf.trim(),
      cin: edition.cin.trim(),
      soldeDepart: (Number(edition.soldeDepartMontant) || 0) * (edition.soldeDepartSens === 'du_tiers' ? -1 : 1),
      soldeDepartDate: edition.soldeDepartDate,
      soldeDepartNote: edition.soldeDepartNote.trim(),
      ...(edition.personnel ? {} : { exempteMinimumTrituration: edition.exempteMinimumTrituration === true }),
      ...(admin && edition.estClient && !edition.personnel ? { tarifTriturationPersonnalise: tarifClient } : {}),
      ...(edition.personnel
        ? {
            cnss: edition.cnss.trim(),
            contrat: edition.contrat,
            salaireJour: Number(edition.salaireJour) || 0,
            salaireMois: Number(edition.salaireMois) || 0,
          }
        : {}),
    }));
    if (refus) return;
    toast.success(`Fiche de ${edition.nom.trim()} mise à jour`);
    setEdition(null);
    load();
  };

  const q = recherche.trim().toLowerCase();
  const supprimer = async (c) => {
    if (!window.confirm(`Supprimer définitivement la fiche « ${c.nom} » ?`)) return;
    const r = await deleteContact(c.id);
    if (r.error) return toast.error(r.error);
    toast.success(`Fiche « ${c.nom} » supprimée`);
    load();
  };
  const parOnglet =
    onglet === 'tous'
      ? contacts
      : onglet === 'associe'
        ? contacts.filter((c) => c.estAssocie)
        : onglet === 'fournisseur_general'
          ? contacts.filter((c) => c.typeTiers === 'fournisseur_general')
          : contacts.filter((c) => c.categorie === onglet);
  const visibles = q
    ? parOnglet.filter((c) =>
        [c.code, c.nom, c.region, c.tel, c.mf, c.cin, c.codeClient, c.codeFournisseur].some((v) => String(v || '').toLowerCase().includes(q))
      )
    : parOnglet;

  const compte = (id) =>
    id === 'tous'
      ? contacts.length
      : id === 'associe'
        ? contacts.filter((c) => c.estAssocie).length
        : id === 'fournisseur_general'
          ? contacts.filter((c) => c.typeTiers === 'fournisseur_general').length
          : contacts.filter((c) => c.categorie === id).length;

  return (
    <div data-testid="contacts-page" className="space-y-6">
      <PageTitle
        emoji="👥"
        titre="Contacts"
        sous="Base unifiée — un même contact peut cumuler les rôles client (prestation) et fournisseur (achat d'olives)"
        action={
          <div className="flex flex-wrap gap-2">
            {admin && <ImportContactsButton onDone={load} />}
            <button
              type="button"
              data-testid="nouveau-contact-page"
              onClick={() => setNouveau(true)}
              className="flex h-11 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10"
            >
              <UserPlus size={16} /> Nouveau contact
            </button>
          </div>
        }
      />

      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="recherche-contact"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Rechercher par nom, région, téléphone, MF ou CIN…"
          className="h-11 w-full border border-slate-300 bg-white pl-9 pr-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900"
        />
      </div>

      <div className="flex flex-wrap gap-px bg-slate-200">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`filtre-contact-${o.id}`}
            onClick={() => setOnglet(o.id)}
            className={`px-4 py-2.5 text-left transition-colors duration-150 ${
              onglet === o.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <span className="block text-sm font-bold">
              {o.label} <span className="font-data text-xs opacity-70">({compte(o.id)})</span>
            </span>
            <span className={`text-xs ${onglet === o.id ? 'text-slate-300' : 'text-slate-500'}`}>{o.hint}</span>
          </button>
        ))}
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="contacts-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Code', 'Nom', 'Région', 'Téléphone', 'Adresse', 'MF / CIN', 'Client', 'Fournisseur', 'Min. trituration', 'Type de tiers', 'Catégorie', 'Actions'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr data-testid="contacts-empty">
                  <td colSpan={12} className="px-3 py-12 text-center text-sm text-slate-500">
                    Aucun contact dans cette vue.
                  </td>
                </tr>
              ) : (
                visibles.map((c) => (
                  <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td data-testid={`code-contact-${c.id}`} className="px-3 py-2 font-data text-xs font-bold text-slate-600">{c.code || '—'}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-slate-900">
                      {c.nom}
                      {c.estClient && Number(c.tarifTriturationPersonnalise) > 0 && (
                        <span data-testid={`tarif-client-${c.id}`} className="mt-1 block font-data text-xs font-semibold text-emerald-800">
                          Tarif spécial : {c.tarifTriturationPersonnalise} TND/kg
                        </span>
                      )}
                      {c.estAssocie ? (
                        <span data-testid={`role-associe-${c.id}`} className="ml-1.5 border border-slate-900 bg-slate-900 px-1.5 py-0.5 font-data text-[10px] uppercase tracking-[0.08em] text-white">
                          Associé
                        </span>
                      ) : (
                        admin &&
                        c.type !== 'personnel' && (
                          <button
                            type="button"
                            data-testid={`ajouter-role-associe-${c.id}`}
                            onClick={async () => {
                              const { error } = await rattacherAssocie(c.id, user);
                              if (error) return toast.error(error);
                              toast.success(`Rôle Associé ajouté à « ${c.nom} » — identifiant, rôles et historique conservés.`);
                              load();
                            }}
                            className="ml-1.5 border border-slate-300 px-1.5 py-0.5 font-data text-[10px] uppercase tracking-[0.08em] text-slate-500 hover:bg-slate-900 hover:text-white"
                          >
                            + associé
                          </button>
                        )
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">{c.region}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-900">{c.tel || '—'}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{c.adresse || '—'}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">
                      {c.type === 'personnel' ? (
                        <span data-testid={`identite-personnel-${c.id}`}>
                          CIN {c.cin || '—'}
                          <span className="block text-[11px] text-slate-500">CNSS {c.cnss || '—'}</span>
                          <span className="block text-[11px] text-slate-500">{c.contrat || '—'}</span>
                          <span className="block text-[11px] text-slate-500">
                            Jour {fmtTnd(c.salaireJour)} · Mois {fmtTnd(c.salaireMois)}
                          </span>
                        </span>
                      ) : (
                        <>
                          {c.mf || '—'}
                          {c.cin ? <span className="block text-[11px] text-slate-500">CIN {c.cin}</span> : null}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        data-testid={`role-client-${c.id}`}
                        checked={c.estClient}
                        onChange={() => basculer(c, 'estClient')}
                        className="h-4 w-4 accent-slate-900"
                      />
                      {!c.estClient && c.autoClient && (
                        <span className="ml-1.5 font-data text-[10px] uppercase text-amber-700">auto</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        data-testid={`role-fournisseur-${c.id}`}
                        checked={c.estFournisseur}
                        onChange={() => basculer(c, 'estFournisseur')}
                        className="h-4 w-4 accent-slate-900"
                      />
                      {!c.estFournisseur && c.autoFournisseur && (
                        <span className="ml-1.5 font-data text-[10px] uppercase text-amber-700">auto</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {c.type === 'personnel' ? (
                        <span className="font-data text-[10px] uppercase text-slate-400">—</span>
                      ) : (
                        <label className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            data-testid={`exempt-minimum-${c.id}`}
                            disabled={!admin}
                            checked={c.exempteMinimumTrituration === true}
                            onChange={async () => {
                              const exempt = !(c.exempteMinimumTrituration === true);
                              await updateContact(c.id, { exempteMinimumTrituration: exempt });
                              toast.success(
                                exempt
                                  ? `${c.nom} est exempté du minimum de facturation de trituration`
                                  : `${c.nom} est soumis au minimum de facturation de trituration`
                              );
                              load();
                            }}
                            className="h-4 w-4 accent-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          {c.exempteMinimumTrituration === true && (
                            <span
                              data-testid={`badge-exempt-minimum-${c.id}`}
                              className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-data text-[10px] uppercase tracking-[0.08em] text-amber-800"
                            >
                              exempté
                            </span>
                          )}
                        </label>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {c.type === 'personnel' ? (
                        <span className="font-data text-[10px] uppercase text-slate-400">—</span>
                      ) : (
                        <select
                          data-testid={`type-tiers-${c.id}`}
                          value={c.typeTiers ?? ''}
                          onChange={async (e) => {
                            await updateContact(c.id, { typeTiers: e.target.value });
                            toast.success(`${c.nom} classé « ${TYPES_TIERS.find((t) => t.id === e.target.value)?.label} »`);
                            load();
                          }}
                          className="h-9 border border-slate-300 bg-white px-1.5 font-data text-[11px] text-slate-800 outline-none focus:border-slate-900"
                        >
                          {TYPES_TIERS.map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`categorie-${c.id}`}
                        className={`font-data text-[10px] uppercase tracking-[0.08em] ${
                          c.categorie === 'agriculteur'
                            ? 'bg-emerald-50 px-1.5 py-0.5 text-emerald-800'
                            : c.categorie === 'client'
                              ? 'bg-sky-50 px-1.5 py-0.5 text-sky-800'
                              : c.categorie === 'fournisseur'
                                ? 'bg-amber-50 px-1.5 py-0.5 text-amber-800'
                                : 'text-slate-400'
                        }`}
                      >
                        {c.categorie === 'aucun' ? 'non classé' : c.categorie}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          data-testid={`editer-contact-${c.id}`}
                          title="Modifier la fiche"
                          onClick={() =>
                            setEdition({
                              ...c, regionOrigine: c.regionDefault || c.region, variete: c.varieteDefault, gps: c.gpsDefault,
                              id: c.id,
                              code: c.code,
                              nom: c.nom,
                              region: c.region === '—' ? '' : c.region,
                              tel: c.tel || '',
                              adresse: c.adresse || '',
                              mf: c.mf || '',
                              cin: c.cin || '',
                              cnss: c.cnss || '',
                              contrat: c.contrat || TYPES_CONTRAT[0],
                              salaireJour: c.salaireJour ?? '',
                              salaireMois: c.salaireMois ?? '',
                              personnel: c.type === 'personnel',
                              estClient: c.estClient === true,
                              tarifTriturationPersonnalise: c.tarifTriturationPersonnalise ?? '',
                              repriseGeree: c.repriseGeree === true,
                              soldeDepartMontant: c.soldeDepart ? Math.abs(c.soldeDepart) : '',
                              soldeDepartSens: (c.soldeDepart ?? 0) < 0 ? 'du_tiers' : 'a_payer',
                              soldeDepartDate: c.soldeDepartDate || new Date().toISOString().slice(0, 10),
                              soldeDepartNote: c.soldeDepartNote || '',
                              exempteMinimumTrituration: c.exempteMinimumTrituration === true,
                            })
                          }
                          className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          <Pencil size={13} />
                        </button>
                        {admin && (
                          <button
                            type="button"
                            data-testid={`supprimer-contact-${c.id}`}
                            title="Supprimer la fiche"
                            onClick={() => supprimer(c)}
                            className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:border-red-600 hover:bg-red-600 hover:text-white"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                        {c.type !== 'personnel' && (
                          <button
                            type="button"
                            data-testid={`qr-solde-${c.id}`}
                            title="Générer un QR code de solde"
                            onClick={() => setQrSolde(c)}
                            className="inline-flex items-center gap-1.5 border border-emerald-700 px-2 py-1 text-xs font-bold text-emerald-800 transition-colors duration-150 hover:bg-emerald-700 hover:text-white"
                          >
                            <QrCode size={13} /> QR solde
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`extrait-compte-${c.id}`}
                          onClick={() => ouvrirExtrait(c)}
                          className="inline-flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          <BookOpen size={13} /> Extrait de compte
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {livre && <GrandLivreTiers compte={livre} onClose={() => setLivre(null)} />}
      {qrSolde && (
        <QrSoldeDialog tiersId={qrSolde.id} nom={qrSolde.nom} tel={qrSolde.tel} onClose={() => setQrSolde(null)} />
      )}
      {nouveau && (
        <ContactQuickForm
          onClose={() => setNouveau(false)}
          onCreated={() => {
            setNouveau(false);
            load();
          }}
        />
      )}
      {edition && (
        <div data-testid="edition-contact-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
          <form onSubmit={enregistrerEdition} data-testid="edition-contact-form" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
            {!edition.personnel && <ChampsOrigine value={edition} onChange={setEdition} />}
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">Modifier la fiche contact</h2>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">{edition.code} · {edition.nom}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              {(edition.personnel
                ? [
                    ['nom', 'Nom / Prénom'],
                    ['region', 'Poste / Atelier'],
                    ['tel', 'Téléphone'],
                    ['adresse', 'Adresse'],
                    ['cin', 'N° CIN'],
                    ['cnss', 'N° CNSS'],
                    ['salaireJour', 'Salaire journalier (Shift) — TND'],
                    ['salaireMois', 'Salaire mensuel — TND'],
                  ]
                : [
                    ['nom', 'Nom / Raison sociale'],
                    ['region', 'Région'],
                    ['tel', 'Téléphone'],
                    ['adresse', 'Adresse'],
                    ['mf', 'Matricule fiscal (MF)'],
                    ['cin', 'CIN'],
                  ]
              ).map(([k, lib]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{lib}</label>
                  <input
                    data-testid={`edition-${k}`}
                    value={edition[k]}
                    onChange={(e) => setEdition((p) => ({ ...p, [k]: e.target.value }))}
                    className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                  />
                </div>
              ))}
              {edition.personnel && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Type de contrat</label>
                  <select
                    data-testid="edition-contrat"
                    value={edition.contrat}
                    onChange={(e) => setEdition((p) => ({ ...p, contrat: e.target.value }))}
                    className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                  >
                    {TYPES_CONTRAT.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}
              {!edition.personnel && edition.estClient && (
                <div className="sm:col-span-2">
                  <label htmlFor="edition-tarif-client" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                    Tarif de trituration personnalisé (TND/kg)
                  </label>
                  <input id="edition-tarif-client" data-testid="edition-tarif-client" type="number" min="0" step="any"
                    disabled={!admin} value={edition.tarifTriturationPersonnalise}
                    onChange={(e) => setEdition((p) => ({ ...p, tarifTriturationPersonnalise: e.target.value }))}
                    placeholder="Vide : tarif standard par qualité"
                    className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm disabled:cursor-not-allowed disabled:opacity-70" />
                  <p className="mt-1 text-xs text-slate-500">Réservé à l’Administrateur. S’applique aux futurs bons client uniquement ; les bons existants conservent leur tarif.</p>
                </div>
              )}
              {!edition.personnel && (
                <div className="sm:col-span-2">
                  <label
                    data-testid="edition-exempt-minimum"
                    className={`flex cursor-pointer items-start gap-3 border px-3 py-3 transition-colors duration-150 ${
                      edition.exempteMinimumTrituration ? 'border-amber-600 bg-amber-50' : 'border-slate-300 bg-white hover:bg-slate-50'
                    } ${admin ? '' : 'cursor-not-allowed opacity-70'}`}
                  >
                    <input
                      type="checkbox"
                      data-testid="edition-input-exempt-minimum"
                      disabled={!admin}
                      checked={edition.exempteMinimumTrituration === true}
                      onChange={(e) => setEdition((p) => ({ ...p, exempteMinimumTrituration: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 accent-amber-600"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-slate-900">
                        Exempté du minimum de facturation de trituration
                      </span>
                      <span className="mt-0.5 block font-data text-[11px] text-slate-500">
                        Exemption permanente : les prestations de ce client sont toujours facturées au
                        poids réel, sans complément. Modification réservée à l’Administrateur.
                      </span>
                    </span>
                  </label>
                </div>
              )}
              <div className="sm:col-span-2 border-t border-slate-200 pt-4">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-900">Solde de départ (report d’ouverture)</p>                <p className="font-data text-[11px] text-slate-500">Pour ajouter plusieurs dettes et garder leurs corrections : Paramètres → Reprises de départ. Un solde géré par ce registre se corrige uniquement depuis ce registre.</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Montant — TND</label>
                <input
                  data-testid="edition-soldeDepartMontant" disabled={edition.repriseGeree}
                  type="number" step="0.001" min="0"
                  value={edition.soldeDepartMontant}
                  onChange={(e) => setEdition((p) => ({ ...p, soldeDepartMontant: e.target.value }))}
                  className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Sens</label>
                <select
                  data-testid="edition-soldeDepartSens" disabled={edition.repriseGeree}
                  value={edition.soldeDepartSens}
                  onChange={(e) => setEdition((p) => ({ ...p, soldeDepartSens: e.target.value }))}
                  className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                >
                  <option value="a_payer">Nous lui devons (à payer au tiers)</option>
                  <option value="du_tiers">Il nous doit (créance)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Date du report</label>
                <input
                  data-testid="edition-soldeDepartDate" disabled={edition.repriseGeree}
                  type="date"
                  value={edition.soldeDepartDate}
                  onChange={(e) => setEdition((p) => ({ ...p, soldeDepartDate: e.target.value }))}
                  className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Note / justificatif</label>
                <input
                  data-testid="edition-soldeDepartNote" disabled={edition.repriseGeree}
                  value={edition.soldeDepartNote}
                  placeholder="Ex. : reliquat saison 2025, cahier p. 12"
                  onChange={(e) => setEdition((p) => ({ ...p, soldeDepartNote: e.target.value }))}
                  className="mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
              </div>
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button type="submit" data-testid="edition-enregistrer" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700">
                Enregistrer les modifications
              </button>
              <button type="button" data-testid="edition-annuler" onClick={() => setEdition(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
